import { useMemo } from 'react';
import { baseFieldClasses } from '@odo/shared-ui/components/fieldClasses';
import { SearchableSelect, type SearchableSelectOption } from '../shared/SearchableSelect';
import {
    MAX_CUSTOM_TYPE_LENGTH,
    type VirtualAppointmentTypeDto
} from '../../services/virtualAppointments';
import { FieldLabel } from './VirtualAppointmentFieldParts';

const CUSTOM_OPTION = '__custom__';

export interface VirtualAppointmentTypeOptions {
    types: VirtualAppointmentTypeDto[];
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}

interface VirtualAppointmentTypeFieldProps {
    inputId: string;
    typeMode: 'code' | 'custom';
    typeCode: string;
    typeCustom: string;
    options: VirtualAppointmentTypeOptions;
    error?: string;
    onSelectCode: (code: string) => void;
    onSelectCustom: () => void;
    onCustomChange: (value: string) => void;
}

// A retired type stays selectable on the VA that already uses it; custom sits apart at the end.
function buildTypeOptions(
    options: VirtualAppointmentTypeOptions,
    typeCode: string
): SearchableSelectOption[] {
    const isUnlisted = typeCode !== '' && !options.types.some((t) => t.code === typeCode);
    const typesLoaded = !options.isLoading && !options.isError;
    return [
        ...options.types.map((type) => ({
            value: type.code,
            label: type.code,
            ...(type.label !== type.code && { sublabel: type.label })
        })),
        ...(isUnlisted
            ? [{ value: typeCode, label: typesLoaded ? `${typeCode} (retired)` : typeCode }]
            : []),
        { value: CUSTOM_OPTION, label: 'Custom type…', separatedAbove: true }
    ];
}

export function VirtualAppointmentTypeField({
    inputId,
    typeMode,
    typeCode,
    typeCustom,
    options,
    error,
    onSelectCode,
    onSelectCustom,
    onCustomChange
}: VirtualAppointmentTypeFieldProps) {
    const errorClasses = error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : '';
    const typeOptions = useMemo(() => buildTypeOptions(options, typeCode), [options, typeCode]);

    return (
        <div className="flex flex-col">
            <FieldLabel htmlFor={inputId} required error={error}>
                Type
            </FieldLabel>
            <SearchableSelect
                mode="single"
                id={inputId}
                label="Type"
                value={typeMode === 'custom' ? CUSTOM_OPTION : typeCode || null}
                options={typeOptions}
                placeholder={options.isLoading ? 'Loading types…' : 'Select a type…'}
                searchPlaceholder="Search a type"
                disabled={options.isLoading}
                triggerClassName={`${baseFieldClasses} ${errorClasses} h-9`}
                onChange={(value) =>
                    value === CUSTOM_OPTION ? onSelectCustom() : onSelectCode(value)
                }
            />
            {typeMode === 'custom' && (
                <input
                    type="text"
                    value={typeCustom}
                    onChange={(e) => onCustomChange(e.target.value)}
                    maxLength={MAX_CUSTOM_TYPE_LENGTH}
                    placeholder="Describe the check"
                    aria-label="Custom type"
                    className={`${baseFieldClasses} ${errorClasses} h-9 mt-2`}
                />
            )}
            {options.isError && (
                <button
                    type="button"
                    onClick={options.onRetry}
                    className="mt-1 text-left text-xs text-red-500 hover:underline"
                >
                    Couldn't load the type list. Retry
                </button>
            )}
        </div>
    );
}
